import { Component, OnInit } from '@angular/core';
import { ContactService } from '../shared/contact.service';
import { Edit, Eye, LucideAngularModule, Plus, Trash } from 'lucide-angular';
import { FeatureToggleService } from '@app/feature-toggles/feature-toggle.service';
import { PagedEntities } from '@app/shared/abstract/paged-entities';
import { RouteParams } from '@app/shared/abstract/route-params';
import { Column } from '@app/partials/datatable/data-table/column';
import { DataTableComponent } from '@app/partials/datatable/data-table/data-table.component';
import { RouterLink } from '@angular/router';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { Destroyer } from '@app/shared/abstract/destroyer';
import { AuthService } from '@app/auth/auth.service';
import { UserModel } from '@app/users/shared/user.model';
import { NotificationService } from '@app/shared/notification/notification.service';
import { ModalComponent } from '@app/partials/modal/modal.component';

@Component({
  selector: 'app-contacts-index',
  imports: [
    DataTableComponent, 
    LucideAngularModule, 
    RouterLink,
    TextInputComponent,
    ReactiveFormsModule,
    ModalComponent,
  ],
  templateUrl: './contacts-index.component.html',
})
export class ContactsIndexComponent extends Destroyer implements OnInit {
  constructor(
    private service: ContactService,
    private authService: AuthService,
    private notificationService: NotificationService,
    private featureToggleService: FeatureToggleService,
  ) {
    super();
  }

  protected readonly PlusIcon = Plus;
  protected readonly EditIcon = Edit;
  protected readonly EyeIcon = Eye;
  protected readonly TrashIcon = Trash;

  // TVG-136: the read View of a contact page lives behind the CRM Sales Flow
  // toggle, so the "View" row action only appears when the toggle is on.
  protected get crmSalesFlowEnabled(): boolean {
    return this.featureToggleService.isEnabled('crm-sales-flow');
  }

  protected usersPage?: PagedEntities<UserModel>;
  protected searchControl = new FormControl('');
  protected currentUser: UserModel | null = null;
  protected canManageContacts = false;
  protected isAdmin = false;
  protected contactToDelete: UserModel | null = null;
  protected isDeleteModalOpen = false;
  protected isDeleting = false;

  routeParams = new RouteParams({
    pageNum: 1,
    pageSize: 100,
  });

  tableColumns: Column<UserModel>[] = [
    { field: 'name', header: 'Name', sortable: true },
    { field: 'email', header: 'Email', sortable: true },
    {
      field: 'phone_numbers',
      header: 'Phone Number(s)',
      callback: (item: UserModel) => item.phone_numbers?.join(', '),
    },
    {
      field: 'address',
      header: 'Address',
      callback: (item: UserModel) => {
        const addressParts = [item.address_line_1, item.address_line_2, item.city, item.postcode, item.country].filter(
          (part) => part,
        );
        return addressParts.join(', ');
      },
    },
    { field: 'created_at', header: 'Created At', sortable: true },
  ];

  // TVG-360: View and Edit opened the same page, so there is one button per row
  // now. Editing happens inside the contact page itself, behind its own Edit
  // button. With the CRM toggle off there is no view route, so the one button
  // opens the editable page instead — one button either way.
  rowActions = [
    {
      icon: this.EyeIcon,
      type: 'link',
      routerLink: (item: UserModel) =>
        this.crmSalesFlowEnabled ? ['/contacts', item.id] : ['/contacts', item.id, 'edit'],
      condition: () => this.crmSalesFlowEnabled || this.canManageContacts,
      title: () => 'Open contact',
    },
    {
      icon: this.TrashIcon,
      callback: (item: UserModel) => this.requestDelete(item),
      condition: () => this.isAdmin,
      classes: 'text-gray-500 hover:text-error-500 dark:text-gray-400 dark:hover:text-error-500',
      title: () => 'Delete',
    },
  ];

  loadData() {
    this.service.getPage(this.routeParams).subscribe((res) => {
      this.usersPage = res;
    });
  }

  ngOnInit(): void {
    this.authService.getUser().pipe(takeUntil(this.destroy$)).subscribe({
      next: (user) => {
        this.currentUser = user;
        this.isAdmin = user?.roles?.includes('admin') ?? false;
        this.canManageContacts = this.isAdmin || (user?.roles?.includes('manager') ?? false);
        this.loadData();
      },
      error: () => {
        this.isAdmin = false;
        this.canManageContacts = false;
        this.loadData();
      },
    });

    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((searchTerm) => {
        this.routeParams.filter = searchTerm ? [{ field: 'search', value: searchTerm }] : [];
        this.routeParams.pageNum = 1;
        this.loadData();
      });
  }

  handleRoute(params: RouteParams) {
    this.routeParams.sort = params.sort;
    this.routeParams.pageNum = params.pageNum;
    this.routeParams.pageSize = params.pageSize;
    this.loadData();
  }

  private requestDelete(contact: UserModel): void {
    if (!contact?.id) {
      return;
    }

    this.contactToDelete = contact;
    this.isDeleteModalOpen = true;
  }

  protected confirmDelete(): void {
    if (!this.contactToDelete?.id || this.isDeleting) {
      return;
    }

    this.isDeleting = true;

    this.service.delete(this.contactToDelete.id.toString()).subscribe({
      next: (response) => {
        this.notificationService.success(response?.message || 'Contact deleted');
        this.isDeleteModalOpen = false;
        this.isDeleting = false;
        this.contactToDelete = null;
        this.loadData();
      },
      error: (error) => {
        this.isDeleting = false;
        this.notificationService.error(error?.error?.message || 'Failed to delete contact');
      },
    });
  }

  protected closeDeleteModal(): void {
    if (this.isDeleting) {
      return;
    }

    this.isDeleteModalOpen = false;
    this.contactToDelete = null;
  }
}
